import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAssets } from 'hooks';

import { Asset, AssetsKeys, AssetStatuses, AssetDate } from 'hooks/useAssets/useAssets.types';

import { BatteryList } from './BatteryList';
import { BatteryFilters, ExtendedAsset } from './BatteryList.types';

const ifContainText = (field: string | number | undefined, value: string): boolean => {
  return !!field && field.toString().toLowerCase().indexOf(value.toLowerCase()) > -1;
};

const isBetweenDates = (date: string, from: Date, to: Date): boolean => {
  return !!date && dayjs(date).isAfter(dayjs(from)) && dayjs(date).isBefore(dayjs(to));
};

const mapDateFieldByStatus: Record<AssetStatuses, AssetDate> = {
  [AssetStatuses.ACTIVE]: 'dateOfRegistration' as AssetDate,
  [AssetStatuses.INACTIVE]: 'pickupDate' as AssetDate,
  [AssetStatuses.INSTALLED]: 'dateOfInstallation' as AssetDate,
  [AssetStatuses.VERIFIED]: 'dateOfVerification' as AssetDate,
  [AssetStatuses.ISSUED]: 'dateOfIssue' as AssetDate,
};

export const BatteryListContainer = () => {
  const { state } = useLocation<{ statuses?: AssetStatuses[] }>();
  const { assets, getAssets } = useAssets();
  const [isLoading, setLoading] = useState(false);

  const [filteredAssets, setFilteredAssets] = useState<Asset[]>(assets ?? []);

  useEffect(() => {
    setLoading(true);
    getAssets();
  }, [getAssets]);

  useEffect(() => {
    if (assets) {
      setLoading(false);
      setFilteredAssets(
        assets.filter(battery => {
          if (state?.statuses) {
            return state?.statuses.length === 0 || state?.statuses.includes(battery.status);
          }

          return true;
        }),
      );
    }
  }, [assets, setFilteredAssets, state]);

  const onFiltersChange = (filters: BatteryFilters) => {
    setFilteredAssets(
      (assets ?? []).filter(battery => {
        const batteryDate = battery[mapDateFieldByStatus[battery.status]] ?? '';

        const containsStatus = filters.status.length === 0 || filters.status.includes(battery.status);
        const containsText = ['origin', 'id', 'manufacturer'].reduce((result: boolean, field: string) => {
          return result || ifContainText(battery[field as AssetsKeys] as string, filters.search);
        }, false);
        const betweenDates =
          !(filters.date.from && filters.date.to) || isBetweenDates(batteryDate, filters.date.from, filters.date.to);
        return containsText && containsStatus && betweenDates;
      }),
    );
  };

  const mappedAssets: ExtendedAsset[] = filteredAssets.map(asset => {
    const dateField = mapDateFieldByStatus[asset.status];
    return {
      ...asset,
      date: asset[dateField],
    };
  });

  return (
    <BatteryList
      batteryList={mappedAssets}
      onFiltersChange={onFiltersChange}
      initialStatuses={state?.statuses}
      loading={isLoading}
    />
  );
};
